/**
 * LR Direct Block 5 Tier 1 — Credit App Fill Panel
 * Manual on-demand generation of a filled broker credit application
 * from saved field mappings + the awarded shipper credit profile.
 *
 * Fillable PDFs only. No signatures, no email, no AI.
 */

import { useMemo } from "react";
import { format } from "date-fns";
import {
  ClipboardCheck, CheckCircle2, AlertTriangle, Loader2, Download, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useCreditAppFill, type EligibilityReason, type CreditAppFillRow } from "@/hooks/useCreditAppFill";

interface Props {
  loadId: string;
  className?: string;
}

const REASON_COPY: Record<string, { label: string; tone: "neutral" | "warn" | "error" }> = {
  load_not_found: { label: "Load not found", tone: "error" },
  load_not_awarded: { label: "Load not awarded yet", tone: "neutral" },
  no_winning_bid: { label: "No winning bid recorded", tone: "neutral" },
  winning_bid_not_accepted: { label: "Winning bid is not in an accepted state", tone: "warn" },
  no_active_template: { label: "Broker has no active credit app template", tone: "warn" },
  template_not_fillable: { label: "Broker's template is not a fillable PDF — ask them to re-upload", tone: "warn" },
  no_shipper_profile: { label: "Shipper has not completed a credit profile", tone: "warn" },
  shipper_company_missing: { label: "Awarded load is missing shipper company", tone: "error" },
  forbidden: { label: "Not authorized to generate for this load", tone: "error" },
  db_error: { label: "Database error", tone: "error" },
  template_download_failed: { label: "Could not load broker template", tone: "error" },
  generation_failed: { label: "PDF generation failed", tone: "error" },
  storage_failed: { label: "Could not store generated PDF", tone: "error" },
  ai_failed: { label: "AI mapping failed. Please try again.", tone: "error" },
  ai_rate_limited: { label: "AI is busy right now — please try again in a moment.", tone: "warn" },
  ai_credits_exhausted: { label: "AI credits exhausted — contact support.", tone: "error" },
  unexpected: { label: "Unexpected error", tone: "error" },
  load_id_required: { label: "Missing load id", tone: "error" },
};

function ReasonBadge({ reason }: { reason: EligibilityReason | string }) {
  const r = REASON_COPY[reason] ?? { label: reason, tone: "neutral" as const };
  const variant: "secondary" | "destructive" | "outline" =
    r.tone === "error" ? "destructive" : r.tone === "warn" ? "outline" : "secondary";
  return <Badge variant={variant} className="text-[11px]">{r.label}</Badge>;
}

export function CreditAppFillPanel({ loadId, className }: Props) {
  const {
    eligibility, eligibilityLoading,
    fills, fillsLoading,
    generating, generate,
    downloadFill,
  } = useCreditAppFill(loadId);

  const latest = fills[0];
  const justGenerated = useMemo(
    () => latest && Date.now() - new Date(latest.generated_at).getTime() < 5000,
    [latest],
  );

  const canGenerate = !!eligibility?.ok && !generating;

  const handleGenerate = async () => {
    const res = await generate();
    if (res.ok) {
      toast.success("Filled credit app generated");
    } else if (res.reason) {
      const copy = REASON_COPY[res.reason]?.label ?? res.reason;
      toast.error(`Generation blocked: ${copy}`);
    }
  };

  const handleDownload = async (f: CreditAppFillRow) => {
    const url = await downloadFill(f);
    if (!url) {
      toast.error("Could not get download link");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={cn("rounded-lg border border-border bg-card p-4", className)}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-bold uppercase tracking-wide text-foreground">
            Broker Credit Application
          </h4>
          <Badge variant="secondary" className="text-[10px]">AI auto-fill</Badge>
        </div>
      </div>

      {/* Eligibility */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        {eligibilityLoading ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking eligibility…
          </span>
        ) : eligibility?.ok ? (
          <>
            <span className="flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Ready to generate
            </span>
            {typeof eligibility.field_count === "number" && (
              <Badge variant="secondary" className="text-[10px]">
                {eligibility.field_count} PDF fields
              </Badge>
            )}
          </>
        ) : (
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <ReasonBadge reason={eligibility?.reason ?? "unexpected"} />
            {eligibility?.message && (
              <span className="text-muted-foreground">{eligibility.message}</span>
            )}
          </span>
        )}
      </div>

      {/* Freshness metadata (proves live shipper profile read) */}
      {eligibility?.ok && (eligibility.shipper_profile_updated_at || eligibility.latest_fill_generated_at) && (
        <div className="mb-3 space-y-1 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground">
          {eligibility.shipper_profile_updated_at && (
            <div>
              Shipper credit profile last updated{" "}
              <span className="font-medium text-foreground">
                {format(new Date(eligibility.shipper_profile_updated_at), "MMM d, yyyy h:mm a")}
              </span>
              {typeof eligibility.shipper_profile_populated_field_count === "number" && (
                <> · {eligibility.shipper_profile_populated_field_count} fields populated</>
              )}
            </div>
          )}
          {eligibility.latest_fill_is_stale && (
            <div className="flex items-start gap-1.5 text-amber-700">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                The most recent generated PDF is older than the shipper's current credit profile.
                Generate again to reflect the latest data.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action */}
      <div className="flex items-center gap-2 mb-3">
        <Button
          onClick={handleGenerate}
          disabled={!canGenerate}
          size="sm"
          className="gap-2"
        >
          {generating ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
          ) : (
            <><FileText className="h-4 w-4" /> Generate fresh credit app</>
          )}
        </Button>
        {generating && (
          <span className="text-[11px] text-muted-foreground">
            AI is mapping fields — this takes a few seconds
          </span>
        )}
      </div>


      {/* Current filled application (only one is kept per load) */}
      {fillsLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : !latest ? (
        <div className="text-xs text-muted-foreground">No filled PDF generated yet for this load.</div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Current filled application
          </div>
          <div
            className={cn(
              "flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2",
              justGenerated && "ring-2 ring-emerald-400",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {latest.status === "generated" ? (
                  <Badge variant="secondary" className="text-[10px]">
                    {justGenerated ? "Just generated" : "Generated"}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="text-[10px]">Failed</Badge>
                )}
                <span className="text-xs text-muted-foreground tabular-nums">
                  {format(new Date(latest.generated_at), "MMM d, yyyy h:mm a")}
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                {latest.status === "generated" ? (
                  <>Filled {latest.filled_field_count} · Skipped {latest.skipped_field_count}
                  {latest.unsupported_types?.length > 0 && (
                    <> · Unsupported: {latest.unsupported_types.join(", ")}</>
                  )}</>
                ) : (
                  <>{latest.error_message || "Unknown error"}</>
                )}
              </div>
            </div>
            {latest.status === "generated" && latest.storage_path && (
              <Button size="sm" variant="outline" className="gap-1" onClick={() => handleDownload(latest)}>
                <Download className="h-3.5 w-3.5" /> Download
              </Button>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">
            Regenerating replaces this file with a fresh copy.
          </div>
        </div>
      )}
    </div>
  );
}
